import type { MobileContractScreenId, SurfaceDocument, ToolCall } from '@galapagos/shared';
import { colors } from '@galapagos/design-system/colors';
import { radius } from '@galapagos/design-system/radius';
import { sizing } from '@galapagos/design-system/sizing';

/**
 * The talk-or-text choice that opens the member's first conversation with Sona.
 *
 * One document serves both mount points — the last onboarding step and the
 * re-onboarding voice step — so only the screen identity varies. The document
 * names intents; the host owns the permission dialog, the finalize write, the
 * routing, and the settings deep link (ADR-046 Rules 5-7).
 *
 * State lives under `/conversation_mode` in the host's store:
 * - `selection` — `talk` | `text`, seeded `talk`
 * - `mic_denied` / `mic_sheet_open` — a refused microphone and its sheet
 * - `start_pending` / `start_failed` — the finalize lifecycle
 *
 * Every Sona conversation needs the microphone, text included: the session
 * publishes a muted microphone track while the member types, and the device
 * refuses to create that track without permission. So both Start buttons ask
 * first, and a refusal opens the sheet instead of starting. The sheet offers
 * Settings, or a way forward without Sona: the manual path, which needs no
 * microphone.
 *
 * A chain cannot branch on state (ADR-046 Rule 8), so each mode carries its own
 * Start button and only the one matching the selection is visible.
 */

export const CONVERSATION_MODE_SURFACE_ID = 'conversation-mode';

export const CONVERSATION_MODE_PATHS = {
  selection: '/conversation_mode/selection',
  micDenied: '/conversation_mode/mic_denied',
  micSheetOpen: '/conversation_mode/mic_sheet_open',
  startPending: '/conversation_mode/start_pending',
  startFailed: '/conversation_mode/start_failed',
  consentRequired: '/conversation_mode/consent_required',
} as const;

export const CONVERSATION_MODE_TEST_IDS = {
  back: 'conversation-mode-back',
  talk: 'conversation-mode-talk',
  text: 'conversation-mode-text',
  start: 'conversation-mode-start',
  startText: 'conversation-mode-start-text',
  micSheet: 'conversation-mode-mic-sheet',
  micSheetBody: 'conversation-mode-mic-sheet-body',
  micSheetContinueWithoutSona: 'conversation-mode-mic-sheet-continue-without-sona',
  openSettings: 'conversation-mode-open-settings',
  startError: 'conversation-mode-start-error',
} as const;

export interface ConversationModeCopy {
  backAccessibilityLabel: string;
  title: string;
  subtitle: string;
  talkLabel: string;
  talkTitle: string;
  talkBody: string;
  textLabel: string;
  textTitle: string;
  textBody: string;
  start: string;
  startAccessibilityHint: string;
  micSheetTitle: string;
  micSheetBody: string;
  micSheetContinueWithoutSona: string;
  openSettings: string;
  startError: string;
}

const selectedWhen = (mode: 'talk' | 'text') => ({
  $cond: { $state: CONVERSATION_MODE_PATHS.selection, eq: mode },
  $then: true,
  $else: false,
});

const selectTool = (mode: 'talk' | 'text'): ToolCall => ({
  tool_name: 'set_state',
  parameters: { path: CONVERSATION_MODE_PATHS.selection, value: mode },
});

const setMicSheetOpen = (value: boolean): ToolCall => ({
  tool_name: 'set_state',
  parameters: { path: CONVERSATION_MODE_PATHS.micSheetOpen, value },
});

/**
 * Present the Sona consent gate for the chosen mode. The gate records the
 * decision and finalizes itself (agreeing starts the mode, declining takes
 * the manual path), so the chain ends here: consent comes before the
 * microphone, which only the conversation ever needs.
 */
const consentGatedStart = (): ToolCall => ({
  // The chosen mode travels in the document state the host reads; the
  // catalogued gate tool takes no parameters.
  tool_name: 'open_ai_consent_gate',
  parameters: {},
});

/**
 * Finalize with no conversation and take the manual path. The host owns the
 * finalize and where the member lands; a host continuation (the re-onboarding
 * journey's advance to its transfer) runs only after the finalize landed.
 */
const continueWithoutSona = (outcome?: ConversationModeStartOutcome): ToolCall => ({
  tool_name: 'continue_without_sona',
  ...outcome,
});

/** Ask for the microphone, then start; a refusal opens the sheet instead. */
const micGatedStart = (
  mode: 'talk' | 'text',
  startOutcome?: ConversationModeStartOutcome,
): ToolCall => ({
  tool_name: 'request_microphone_permission',
  onSuccess: [{ tool_name: 'start_intake_conversation', parameters: { mode }, ...startOutcome }],
  onError: [
    {
      tool_name: 'set_state',
      parameters: { path: CONVERSATION_MODE_PATHS.micDenied, value: true },
    },
    setMicSheetOpen(true),
  ],
});

const startButtonState = {
  $cond: { $state: CONVERSATION_MODE_PATHS.startPending, eq: true },
  $then: 'Loading',
  $else: 'Default',
};

/**
 * One Start button. Each mode has two: the ordinary one asks for the
 * microphone and starts, and the gated one presents the Sona consent gate
 * first. The host decides which shows through `consent_required` in the
 * document state: a member whose standing is not yet a grant meets the gate
 * before the microphone, so a refused microphone never stands between them and
 * Continue without Sona. An unset flag reads as not required, so a host that
 * never gates (one that collected consent on an earlier screen) needs no state.
 * Both share a testID because exactly one is ever visible.
 */
function startButton({
  copy,
  startButtonTestId,
  mode,
  gated,
  press,
}: {
  copy: ConversationModeCopy;
  startButtonTestId: string;
  mode: 'talk' | 'text';
  gated: boolean;
  press: ToolCall;
}) {
  return {
    type: 'Button' as const,
    props: {
      size: 'L',
      type: 'Primary',
      state: startButtonState,
      icon: false,
      text: true,
      label: copy.start,
      accessibilityHint: copy.startAccessibilityHint,
      actionId: 'conversation-mode.start.activate',
      testID: startButtonTestId,
    },
    visible: {
      $and: [
        { $state: CONVERSATION_MODE_PATHS.selection, eq: mode },
        gated
          ? { $state: CONVERSATION_MODE_PATHS.consentRequired, eq: true }
          : { $state: CONVERSATION_MODE_PATHS.consentRequired, neq: true },
      ],
    },
    on: { press: [press] },
  };
}

export interface ConversationModeStartOutcome {
  onSuccess?: ToolCall[];
  onError?: ToolCall[];
}

export function buildConversationModeSurface({
  screenId,
  copy,
  startOutcome,
  continueWithoutSonaOutcome,
}: {
  screenId: MobileContractScreenId;
  copy: ConversationModeCopy;
  /** Optional host-specific continuation; the default surface still starts directly. */
  startOutcome?: ConversationModeStartOutcome;
  /**
   * Optional continuation after Continue without Sona's finalize. A host whose
   * own exit routes the manual path (re-onboarding, which still runs its
   * transfer) supplies it; a host that lands the member on the manual path
   * from the finalize itself leaves it unset.
   */
  continueWithoutSonaOutcome?: ConversationModeStartOutcome;
}): SurfaceDocument {
  return {
    schema_version: '1.0',
    surface_id: CONVERSATION_MODE_SURFACE_ID,
    screen_id: screenId,
    locale: 'en',
    fallback: { type: 'host' },
    spec: {
      root: 'screen',
      elements: {
        screen: {
          type: 'Screen',
          props: {
            screenId,
            accessibilityTitle: copy.title,
            // Bare title text above the cards governs the gutter (ADR-091 Rule 3).
            contentPaddingHorizontal: 'lg',
            footerPaddingHorizontal: 'lg',
            // The back control sits just under the status bar, as in the design;
            // the safe-area inset already clears it.
            contentPaddingTop: 'none',
            scrollable: true,
            hasFooter: true,
            footerDivider: false,
          },
          children: ['content', 'footer'],
        },
        content: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'xl' },
          children: ['header', 'options', 'micSheet'],
        },
        header: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'lg', align: 'start' },
          children: ['back', 'heading'],
        },
        back: {
          type: 'BackButton',
          props: {
            accessibilityLabel: copy.backAccessibilityLabel,
            testID: CONVERSATION_MODE_TEST_IDS.back,
          },
          on: { press: [{ tool_name: 'go_back' }] },
        },
        heading: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 's' },
          children: ['title', 'subtitle'],
        },
        title: {
          type: 'Text',
          props: { variant: 'h1Bold28', color: 'primary', value: copy.title, role: 'header' },
        },
        subtitle: {
          type: 'Text',
          props: { variant: 'b1Regular', color: 'primary', value: copy.subtitle },
        },
        options: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 's' },
          children: ['talkCard', 'textCard'],
        },
        talkCard: {
          type: 'BeanCard',
          props: {
            label: copy.talkLabel,
            title: copy.talkTitle,
            body: copy.talkBody,
            emphasis: 'featured',
            selected: selectedWhen('talk'),
            actionId: 'conversation-mode.talk-option.activate',
            testID: CONVERSATION_MODE_TEST_IDS.talk,
          },
          fallback: 'talkCardFallback',
          on: { press: [selectTool('talk')] },
        },
        talkCardFallback: {
          type: 'ListitemCard',
          props: {
            eyebrow: copy.talkLabel,
            title: copy.talkTitle,
            body: copy.talkBody,
            trailer: 'radio',
            selected: selectedWhen('talk'),
            actionId: 'conversation-mode.talk-option.activate',
            testID: CONVERSATION_MODE_TEST_IDS.talk,
          },
          on: { press: [selectTool('talk')] },
        },
        textCard: {
          type: 'BeanCard',
          props: {
            label: copy.textLabel,
            title: copy.textTitle,
            body: copy.textBody,
            emphasis: 'standard',
            selected: selectedWhen('text'),
            actionId: 'conversation-mode.text-option.activate',
            testID: CONVERSATION_MODE_TEST_IDS.text,
          },
          fallback: 'textCardFallback',
          on: { press: [selectTool('text')] },
        },
        textCardFallback: {
          type: 'ListitemCard',
          props: {
            eyebrow: copy.textLabel,
            title: copy.textTitle,
            body: copy.textBody,
            trailer: 'radio',
            selected: selectedWhen('text'),
            actionId: 'conversation-mode.text-option.activate',
            testID: CONVERSATION_MODE_TEST_IDS.text,
          },
          on: { press: [selectTool('text')] },
        },
        footer: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'sm' },
          children: ['startError', 'startTalk', 'startText', 'startTalkGated', 'startTextGated'],
        },
        micSheet: {
          type: 'SmallSheet',
          props: {
            visible: {
              $cond: { $state: CONVERSATION_MODE_PATHS.micSheetOpen, eq: true },
              $then: true,
              $else: false,
            },
            accessibilityLabel: copy.micSheetTitle,
            hasFooter: true,
          },
          children: ['micSheetContent', 'micSheetActions'],
          on: { dismiss: [setMicSheetOpen(false)] },
        },
        // Matches the app's sheets that lead with art: the content is inset to
        // line up with the footer buttons, and the heading sits under the art
        // instead of in the sheet's own title bar.
        micSheetContent: {
          type: 'Stack',
          props: {
            direction: 'vertical',
            gap: 'sm',
            paddingHorizontal: 'sm',
            paddingVertical: 'm',
            align: 'stretch',
          },
          children: ['micSheetIconRow', 'micSheetTitle', 'micSheetBodyBlock'],
        },
        micSheetIconRow: {
          type: 'Stack',
          props: { direction: 'horizontal', justify: 'center', paddingVertical: 's' },
          children: ['micSheetIcon'],
        },
        micSheetIcon: {
          type: 'IconTile',
          props: {
            backgroundColor: colors.mossGreen['300'],
            iconName: 'mic',
            iconTone: 'primary',
            iconSize: sizing.icon.md,
            width: sizing.controlButton.lg,
            height: sizing.controlButton.lg,
            radius: radius.rounded,
          },
        },
        micSheetTitle: {
          type: 'Text',
          props: {
            variant: 'b1Bold',
            color: 'primary',
            align: 'center',
            role: 'header',
            value: copy.micSheetTitle,
          },
        },
        micSheetActions: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'sm', align: 'stretch' },
          children: ['openSettings', 'micSheetContinueWithoutSona'],
        },
        micSheetContinueWithoutSona: {
          type: 'TextButton',
          props: {
            label: copy.micSheetContinueWithoutSona,
            actionId: 'conversation-mode.continue-without-sona.activate',
            testID: CONVERSATION_MODE_TEST_IDS.micSheetContinueWithoutSona,
          },
          on: { press: [continueWithoutSona(continueWithoutSonaOutcome)] },
        },
        micSheetBodyBlock: {
          type: 'Stack',
          props: { direction: 'vertical', paddingVertical: 's' },
          children: ['micSheetBody'],
        },
        micSheetBody: {
          type: 'Text',
          props: {
            variant: 'b2Regular',
            color: 'primary',
            align: 'center',
            value: copy.micSheetBody,
            testID: CONVERSATION_MODE_TEST_IDS.micSheetBody,
          },
        },
        openSettings: {
          type: 'Button',
          props: {
            size: 'L',
            type: 'Primary',
            state: 'Default',
            icon: false,
            text: true,
            label: copy.openSettings,
            actionId: 'conversation-mode.open-settings.activate',
            testID: CONVERSATION_MODE_TEST_IDS.openSettings,
          },
          on: { press: [{ tool_name: 'open_app_settings' }] },
        },
        startError: {
          type: 'Text',
          props: {
            variant: 'b2Regular',
            color: 'alert',
            role: 'alert',
            value: copy.startError,
            testID: CONVERSATION_MODE_TEST_IDS.startError,
          },
          visible: { $state: CONVERSATION_MODE_PATHS.startFailed },
        },
        startTalk: startButton({
          copy,
          startButtonTestId: CONVERSATION_MODE_TEST_IDS.start,
          mode: 'talk',
          gated: false,
          press: micGatedStart('talk', startOutcome),
        }),
        startText: startButton({
          copy,
          startButtonTestId: CONVERSATION_MODE_TEST_IDS.startText,
          mode: 'text',
          gated: false,
          press: micGatedStart('text', startOutcome),
        }),
        startTalkGated: startButton({
          copy,
          startButtonTestId: CONVERSATION_MODE_TEST_IDS.start,
          mode: 'talk',
          gated: true,
          press: consentGatedStart(),
        }),
        startTextGated: startButton({
          copy,
          startButtonTestId: CONVERSATION_MODE_TEST_IDS.startText,
          mode: 'text',
          gated: true,
          press: consentGatedStart(),
        }),
      },
    },
  };
}
